import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  ChevronDown, ChevronRight, Copy as CopyIcon, Check as CheckIcon,
  ShieldCheck, DollarSign, Clock, User as UserIcon, Settings as SettingsIcon,
  FileText, Mail, LogIn, Umbrella, Wrench, Truck, Briefcase, AlertTriangle,
  KeyRound, FileSignature, Search,
} from "lucide-react";

/**
 * Human-readable metadata for every `action` string emitted by `log_audit(...)`
 * on the backend. Anything not listed falls back to a title-cased default.
 *
 * tone → drives the left bar + icon background color for at-a-glance scanning.
 */
const ACTION_META = {
  // ---- Auth / access ----
  "auth.login":                { label: "Signed in",                          Icon: LogIn,         tone: "sky",     summary: "Successful login" },
  "auth.logout":               { label: "Signed out",                         Icon: LogIn,         tone: "slate",   summary: "Session ended" },
  "auth.admin_reset_password": { label: "Admin reset employee password",      Icon: KeyRound,      tone: "amber",   summary: "New password issued to employee" },
  "auth.forgot_password":      { label: "Password reset email sent",          Icon: KeyRound,      tone: "sky",     summary: "Employee requested a reset link" },
  "auth.password_reset":       { label: "Password reset completed",           Icon: KeyRound,      tone: "emerald", summary: "Employee set a new password" },
  "auth.admin_rescue":         { label: "Admin rescue used",                  Icon: ShieldCheck,   tone: "rose",    summary: "Break-glass admin account rotated" },
  "auth.bootstrap_super_admin":{ label: "Promoted to Super Admin",            Icon: ShieldCheck,   tone: "indigo",  summary: "Admin self-promoted (idempotent)" },

  // ---- Employees ----
  "employee.create":           { label: "Employee created",                   Icon: UserIcon,      tone: "emerald", summary: "New team member added" },
  "employee.update":           { label: "Employee updated",                   Icon: UserIcon,      tone: "sky",     summary: "Team-member profile changed" },
  "employee.deactivate":       { label: "Employee deactivated",               Icon: UserIcon,      tone: "amber",   summary: "Marked inactive" },
  "employee.activate":         { label: "Employee reactivated",               Icon: UserIcon,      tone: "emerald", summary: "Marked active" },
  "employee.benefits_update":  { label: "Benefits updated",                   Icon: Umbrella,      tone: "sky",     summary: "PTO / benefits config changed" },
  "employee.pto_adjust":       { label: "PTO balance adjusted",               Icon: Umbrella,      tone: "sky",     summary: "Manual +/- to a benefit balance" },

  // ---- Time / punches ----
  "time.admin_edit":           { label: "Time card edited by admin",          Icon: Clock,         tone: "amber",   summary: "Admin edited a punch or lunch" },
  "time.reset_day":            { label: "Day reset by admin",                 Icon: Clock,         tone: "rose",    summary: "Punches for the day wiped" },
  "time.missed_clockout_resolve": { label: "Missed clock-out resolved",       Icon: AlertTriangle, tone: "emerald", summary: "Admin marked the alert as reviewed" },

  // ---- Job entries ----
  "job.admin_edit":            { label: "Job entry edited by admin",          Icon: Briefcase,     tone: "amber",   summary: "Invoice / hours / notes changed" },
  "job.admin_delete":          { label: "Job entry deleted by admin",         Icon: Briefcase,     tone: "rose",    summary: "Job removed from the day" },

  // ---- Daily submissions ----
  "daily.submit":              { label: "Daily submission signed by employee",Icon: FileSignature, tone: "sky",     summary: "Employee finalized the day" },
  "daily.approve":             { label: "Daily submission approved",          Icon: CheckIcon,     tone: "emerald", summary: "Admin approved the day" },
  "daily.reject":              { label: "Daily submission rejected",          Icon: AlertTriangle, tone: "rose",    summary: "Admin sent the day back for edits" },
  "daily.unlock":              { label: "Daily submission unlocked",          Icon: Clock,         tone: "amber",   summary: "Admin re-opened a locked day" },

  // ---- Paystubs ----
  "paystub.generate":          { label: "Paystubs generated",                 Icon: DollarSign,    tone: "emerald", summary: "Weekly payroll run" },
  "paystub.update_deductions": { label: "Paystub deductions updated",         Icon: DollarSign,    tone: "sky",     summary: "Tips/bonus/PTO/taxes edited" },
  "paystub.finalize":          { label: "Paystub sent for signature",         Icon: DollarSign,    tone: "sky",     summary: "Employee will sign next" },
  "paystub.acknowledge":       { label: "Paystub signed by employee",         Icon: FileSignature, tone: "emerald", summary: "Employee acknowledged the paystub" },

  // ---- Payroll export / periods ----
  "payroll.period.approved":   { label: "Pay period approved",                Icon: FileText,      tone: "emerald", summary: "Locked employee edits for the week" },
  "payroll.period.reopened":   { label: "Pay period re-opened",               Icon: FileText,      tone: "amber",   summary: "Admin unlocked the week" },
  "payroll.adjustment":        { label: "Manual pay adjustment",              Icon: DollarSign,    tone: "amber",   summary: "Override on a single row" },
  "payroll.export":            { label: "Payroll CSV exported",               Icon: FileText,      tone: "sky",     summary: "CSV downloaded for payroll provider" },

  // ---- Shop time ----
  "shop_time.approve":         { label: "Shop time approved",                 Icon: Wrench,        tone: "emerald", summary: "Admin approved shop/training hours" },
  "shop_time.reject":          { label: "Shop time rejected",                 Icon: Wrench,        tone: "rose",    summary: "Admin rejected shop/training hours" },

  // ---- Truck inspections ----
  "truck_inspection.submit":   { label: "Truck inspection signed",            Icon: Truck,         tone: "sky",     summary: "Lead tech completed the checklist" },

  // ---- Settings ----
  "settings.update":           { label: "Settings updated",                   Icon: SettingsIcon,  tone: "slate",   summary: "Company/payroll settings changed" },
  "settings.logo_upload":      { label: "Logo uploaded",                      Icon: SettingsIcon,  tone: "sky",     summary: "New company logo" },
  "settings.logo_remove":      { label: "Logo removed",                       Icon: SettingsIcon,  tone: "slate",   summary: "Company logo cleared" },
  "custom_job_field.add":      { label: "Custom job field added",             Icon: SettingsIcon,  tone: "sky",     summary: "New verification field on job entries" },
  "custom_job_field.update":   { label: "Custom job field edited",            Icon: SettingsIcon,  tone: "slate",   summary: "Existing verification field edited" },
  "custom_job_field.delete":   { label: "Custom job field removed",           Icon: SettingsIcon,  tone: "rose",    summary: "Verification field deleted" },

  // ---- Email reminders ----
  "email_reminder.test":       { label: "Test reminder email sent",           Icon: Mail,          tone: "sky",     summary: "Manual test of the reminder pipeline" },
  "email_reminder.run":        { label: "Reminder run triggered",             Icon: Mail,          tone: "sky",     summary: "Admin manually fired the daily reminder loop" },
};

const TONES = {
  sky:     { bar: "bg-sky-500",     dot: "bg-sky-500",     iconBg: "bg-sky-100 text-sky-700" },
  emerald: { bar: "bg-emerald-500", dot: "bg-emerald-500", iconBg: "bg-emerald-100 text-emerald-700" },
  amber:   { bar: "bg-amber-500",   dot: "bg-amber-500",   iconBg: "bg-amber-100 text-amber-800" },
  rose:    { bar: "bg-rose-500",    dot: "bg-rose-500",    iconBg: "bg-rose-100 text-rose-700" },
  slate:   { bar: "bg-slate-400",   dot: "bg-slate-400",   iconBg: "bg-slate-100 text-slate-700" },
  indigo:  { bar: "bg-indigo-500",  dot: "bg-indigo-500",  iconBg: "bg-indigo-100 text-indigo-700" },
};

const ENTITY_LABEL = {
  daily_submission: "Daily submission",
  time_entry: "Time card",
  paystub: "Paystub",
  employee: "Employee",
  user: "User",
  payroll_settings: "Settings",
  email: "Email",
  job_entry: "Job entry",
};

const humanTitle = (action) => {
  const meta = ACTION_META[action];
  if (meta) return meta;
  // Fallback: "some.snake_case" → "Some snake case"
  const label = action
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { label, Icon: FileText, tone: "slate", summary: "" };
};

const shortId = (id) => {
  if (!id) return "";
  // Split on colon (some entities encode "id:date"), then trim UUID
  const [head] = String(id).split(":");
  return head.length > 8 ? head.slice(0, 8) : head;
};

const relativeTime = (iso) => {
  if (!iso) return "";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const s = Math.round((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

/** Renders a JSON snapshot with light key coloring. Handles empty objects. */
const JsonBlock = ({ data, label, tone = "slate" }) => {
  const empty = !data || (typeof data === "object" && Object.keys(data).length === 0);
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    rose:  "bg-rose-50 border-rose-200 text-rose-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  return (
    <div>
      <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">{label}</p>
      <pre className={`text-[11px] font-mono leading-snug rounded-lg border px-3 py-2 overflow-x-auto whitespace-pre-wrap ${tones[tone]}`}>
        {empty ? <span className="italic opacity-60">(none)</span> : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

/** Compact copy-to-clipboard chip for IDs. */
const CopyChip = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex items-center gap-1 rounded-md bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 text-[10px] font-mono text-slate-700 transition-colors"
      title={`Click to copy — ${value}`}
      data-testid={`audit-copy-${label}`}
    >
      <span className="truncate max-w-[140px]">{value}</span>
      {copied ? <CheckIcon className="h-3 w-3 text-emerald-600" /> : <CopyIcon className="h-3 w-3 opacity-60" />}
    </button>
  );
};

const AuditRow = ({ a, open, onToggle, isTechnical }) => {
  const meta = humanTitle(a.action);
  const tone = TONES[meta.tone] || TONES.slate;
  const Icon = meta.Icon;
  const entityLabel = ENTITY_LABEL[a.entity] || a.entity;
  const [entityId, entitySub] = String(a.entity_id || "").split(":");
  const dateFromAfter = a.after?.date;

  return (
    <li
      className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors"
      data-testid={`audit-${a.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-start gap-3"
        data-testid={`audit-row-${a.id}`}
      >
        <div className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${tone.iconBg}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-bold text-slate-900 text-sm truncate">{meta.label}</p>
            <p className="text-[11px] text-slate-500 whitespace-nowrap shrink-0" title={new Date(a.timestamp).toLocaleString()}>
              {relativeTime(a.timestamp)}
            </p>
          </div>
          <p className="text-xs text-slate-600 mt-0.5">
            <span className="font-semibold">{a.user_name}</span>
            <span className="text-slate-400"> · </span>
            <span>{entityLabel}</span>
            {dateFromAfter && (
              <>
                <span className="text-slate-400"> · </span>
                <span className="text-slate-700">{dateFromAfter}</span>
              </>
            )}
            {entitySub && !dateFromAfter && (
              <>
                <span className="text-slate-400"> · </span>
                <span className="text-slate-700">{entitySub}</span>
              </>
            )}
            {isTechnical && entityId && (
              <>
                <span className="text-slate-400"> · </span>
                <span className="font-mono text-slate-500">#{shortId(entityId)}</span>
              </>
            )}
          </p>
          {a.notes && !open && (
            <p className="text-xs text-slate-600 mt-1 italic truncate">&ldquo;{a.notes}&rdquo;</p>
          )}
        </div>
        <div className="text-slate-400 shrink-0 mt-1">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pl-15 space-y-3" data-testid={`audit-detail-${a.id}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-2">Event</p>
              <p className="text-slate-700"><b className="text-slate-900">What:</b> {meta.summary || meta.label}</p>
              {isTechnical && (
                <p className="text-slate-700 mt-1"><b className="text-slate-900">Action code:</b> <span className="font-mono text-[11px] text-slate-500">{a.action}</span></p>
              )}
              <p className="text-slate-700 mt-1"><b className="text-slate-900">When:</b> {new Date(a.timestamp).toLocaleString()}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-2">Actor &amp; target</p>
              <p className="text-slate-700"><b className="text-slate-900">Actor:</b> {a.user_name} <span className="text-slate-500">({a.user_email})</span></p>
              <p className="text-slate-700 mt-1 flex items-baseline gap-1 flex-wrap">
                <b className="text-slate-900 shrink-0">Target:</b> {entityLabel}
                {isTechnical && <CopyChip value={a.entity_id} label={`entity-id-${a.id}`} />}
              </p>
              {a.notes && (
                <p className="text-slate-700 mt-1"><b className="text-slate-900">Note:</b> {a.notes}</p>
              )}
            </div>
          </div>

          {isTechnical && (a.before || a.after) && (Object.keys(a.before || {}).length > 0 || Object.keys(a.after || {}).length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <JsonBlock data={a.before} label="Before" tone="rose" />
              <JsonBlock data={a.after} label="After" tone="emerald" />
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export default function AdminAuditLog() {
  const { user } = useAuth();
  const isTechnical = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api.get("/audit").then((r) => setItems(r.data || []));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items || []).filter((a) => {
      if (category !== "all" && !String(a.action).startsWith(category)) return false;
      if (!q) return true;
      const hay = `${a.action} ${a.user_name} ${a.user_email} ${a.entity} ${a.entity_id} ${a.notes || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, category]);

  const CATEGORIES = [
    { id: "all",      label: "All" },
    { id: "auth",     label: "Auth" },
    { id: "employee", label: "Employees" },
    { id: "time",     label: "Time" },
    { id: "job",      label: "Jobs" },
    { id: "daily",    label: "Submissions" },
    { id: "paystub",  label: "Paystubs" },
    { id: "payroll",  label: "Payroll" },
    { id: "shop_time",label: "Shop time" },
    { id: "settings", label: "Settings" },
  ];

  return (
    <div className="space-y-5" data-testid="admin-audit-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{isTechnical ? "Super Admin" : "Admin"}</p>
        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">
          {isTechnical ? "Audit Log" : "Activity"}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {isTechnical
            ? "Every change made in the app — who did it, when, and what actually changed. Click any row for the full before/after."
            : "Who changed what and when — approvals, edits, and other important actions on your account. Click a row for a plain-English summary."}
        </p>
      </header>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by person, action, note…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="audit-search-input"
              />
            </div>
            <div className="flex gap-1 overflow-x-auto pb-1 sm:pb-0" data-testid="audit-category-chips">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id)}
                  className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                    category === c.id
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`audit-category-${c.id}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-slate-500">
              {items.length === 0 ? "No activity yet." : "No matches for those filters."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="audit-list">
              {filtered.map((a) => (
                <AuditRow
                  key={a.id}
                  a={a}
                  open={openId === a.id}
                  onToggle={() => setOpenId(openId === a.id ? null : a.id)}
                  isTechnical={isTechnical}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
